<template>
  <div class="min-h-full bg-transparent pb-16 text-slate-800 dark:text-[#F1F5F9] font-sans antialiased">
    <!-- SKELETON LOADING -->
    <div v-if="isLoading" class="w-full px-2 py-8 space-y-6">
      <div class="h-44 w-full animate-pulse rounded-2xl sm:rounded-3xl bg-slate-200/80 dark:bg-[#18243A]"></div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="h-64 animate-pulse rounded-2xl sm:rounded-3xl bg-slate-200/80 dark:bg-[#18243A]"></div>
        <div class="h-64 animate-pulse rounded-2xl sm:rounded-3xl bg-slate-200/80 dark:bg-[#18243A]"></div>
        <div class="h-64 animate-pulse rounded-2xl sm:rounded-3xl bg-slate-200/80 dark:bg-[#18243A]"></div>
      </div>
      <div class="h-96 w-full animate-pulse rounded-2xl sm:rounded-3xl bg-slate-200/80 dark:bg-[#18243A]"></div>
    </div>

    <div v-else class="w-full space-y-6 sm:space-y-8 py-1">
      <!-- TOP COMMAND BAR -->
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-3.5 sm:gap-5 bg-white dark:bg-[#111C30] p-3.5 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-xs">
        <div class="min-w-0">
          <div class="flex items-center gap-2 text-[11px] sm:text-xs font-bold text-emerald-700 dark:text-emerald-400 tracking-wider uppercase mb-1">
            <span class="h-2 w-2 rounded-full bg-emerald-500 animate-pulse shrink-0"></span>
            Educanium Web Portal Studio
          </div>
          <h1 class="text-lg sm:text-2xl lg:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight truncate sm:whitespace-normal">
            {{ activeStudioTab === 'overview' ? 'Website Command Center & Template Studio' : 'Header Branding & Navigation Studio' }}
          </h1>
          <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] mt-0.5 truncate sm:whitespace-normal">
            {{ activeStudioTab === 'overview'
              ? 'Real-time portal management, live template visual identity engine, and readiness scorecard.'
              : "Design your school's live header interface with instant real-time canvas preview." }}
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full lg:w-auto">
          <!-- STUDIO VIEW SWITCHER -->
          <div class="w-full sm:w-auto grid grid-cols-2 sm:flex items-center p-1 bg-slate-100 dark:bg-[#18243A] rounded-2xl border border-slate-200 dark:border-[#263449] text-xs font-bold">
            <button
              type="button"
              @click="activeStudioTab = 'overview'"
              class="px-2.5 sm:px-3.5 py-2 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5"
              :class="activeStudioTab === 'overview'
                ? 'bg-white dark:bg-[#111C30] text-slate-900 dark:text-[#F1F5F9] shadow-xs'
                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'"
            >
              <LayoutDashboardIcon class="w-3.5 h-3.5 text-indigo-600 dark:text-blue-400 shrink-0" />
              <span class="truncate">Overview</span>
            </button>
            <button
              type="button"
              @click="activeStudioTab = 'header'"
              class="px-2.5 sm:px-3.5 py-2 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5"
              :class="activeStudioTab === 'header'
                ? 'bg-white dark:bg-[#111C30] text-slate-900 dark:text-[#F1F5F9] shadow-xs'
                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'"
            >
              <PaletteIcon class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span class="truncate">Header Studio</span>
            </button>
          </div>

          <!-- VISIT LIVE WEBSITE BUTTON -->
          <a
            :href="fullPublicUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#1F2E47] hover:text-indigo-600 dark:hover:text-blue-400 transition shadow-xs cursor-pointer active:scale-95"
            title="Open Live Website in New Tab"
          >
            <GlobeIcon class="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>Visit Live Site</span>
            <ExternalLinkIcon class="h-3 w-3 text-slate-400 dark:text-[#7E8DA5] shrink-0" />
          </a>

          <!-- HEADER TAB ACTIONS -->
          <template v-if="activeStudioTab === 'header'">
            <button
              type="button"
              class="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer shadow-xs"
              :disabled="isSaving"
              @click="confirmReset"
            >
              Discard Changes
            </button>
            <button
              type="button"
              class="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#0f2423] dark:bg-emerald-900/60 dark:border dark:border-emerald-700/40 hover:bg-[#163533] dark:hover:bg-emerald-900/80 text-white text-xs font-bold shadow-lg shadow-[#0f2423]/20 transition active:scale-95 cursor-pointer disabled:opacity-50"
              :disabled="isSaving"
              @click="handleSave"
            >
              <Loader2Icon v-if="isSaving" class="h-4 w-4 animate-spin text-[#d4af37]" />
              <SaveIcon v-else class="h-4 w-4 text-[#d4af37]" />
              <span>{{ isSaving ? "Saving..." : "Publish Header" }}</span>
            </button>
          </template>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- VIEW 1: PORTAL OVERVIEW & TEMPLATE STUDIO -->
      <!-- ======================================================== -->
      <div v-if="activeStudioTab === 'overview'" class="space-y-8">
        <!-- 1. LIVE PORTAL STATUS BANNER CARD -->
        <div class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-xs p-4 sm:p-6 lg:p-8 overflow-hidden relative">
          <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <!-- Left Info: Logo, Name, Meta -->
            <div class="flex items-start sm:items-center gap-3.5 sm:gap-5 min-w-0 flex-1">
              <div class="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center justify-center font-black text-lg sm:text-2xl shadow-md border-2 border-slate-100 dark:border-[#263449] shrink-0 overflow-hidden">
                <img
                  v-if="resolvedLogoUrl"
                  :src="resolvedLogoUrl"
                  :alt="form.schoolName"
                  class="w-full h-full object-contain p-1.5"
                />
                <span v-else>{{ schoolInitials }}</span>
              </div>

              <div class="space-y-1 min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <h2 class="text-lg sm:text-2xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight truncate">
                    {{ form.schoolName || 'Your School Name' }}
                  </h2>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10.5px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 shrink-0"
                    :class="websiteStatus === 'active'
                      ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40'
                      : 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-400 border border-rose-200 dark:border-rose-800/40'"
                  >
                    <span
                      class="h-1.5 w-1.5 rounded-full"
                      :class="websiteStatus === 'active' ? 'bg-emerald-600 animate-pulse' : 'bg-rose-600'"
                    ></span>
                    {{ websiteStatus === 'active' ? 'Live & Online' : 'Maintenance' }}
                  </span>
                </div>

                <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] font-medium truncate sm:whitespace-normal">
                  {{ form.tagline || form.boardAffiliation || 'Empowering Students with Quality Education' }}
                </p>

                <!-- Live URL Box -->
                <div class="flex flex-wrap items-center gap-2 pt-1">
                  <div class="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 bg-slate-50 dark:bg-[#172235] rounded-xl border border-slate-200/90 dark:border-[#263449] text-xs font-mono text-slate-700 dark:text-[#F1F5F9] max-w-full">
                    <GlobeIcon class="w-3.5 h-3.5 text-indigo-600 dark:text-blue-400 shrink-0" />
                    <span class="font-bold truncate max-w-[150px] xs:max-w-xs sm:max-w-none">{{ displayDomain }}</span>
                    <span class="text-[10px] text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-1.5 py-0.5 rounded font-sans font-bold inline-flex items-center gap-1 border border-transparent dark:border-emerald-800/40 shrink-0">
                      <ShieldCheckIcon class="w-3 h-3" />
                      SSL Active
                    </span>
                  </div>

                  <button
                    type="button"
                    @click="copyPublicUrl"
                    class="p-1.5 text-slate-500 dark:text-[#7E8DA5] hover:text-indigo-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-lg transition shrink-0"
                    title="Copy Portal Link"
                  >
                    <CopyIcon class="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Right Controls: Toggle Website & Active Template Badge -->
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 border-t lg:border-t-0 lg:border-l border-slate-100 dark:border-[#263449] pt-4 lg:pt-0 lg:pl-6">
              <!-- Active Template Pill -->
              <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-2xl border border-slate-200/80 dark:border-[#263449] text-left">
                <span class="text-[10px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider block">Active Visual Theme</span>
                <span class="text-xs font-black text-slate-900 dark:text-[#F1F5F9] flex items-center gap-1.5 mt-0.5">
                  <PaletteIcon class="w-3.5 h-3.5 text-indigo-600 dark:text-blue-400" />
                  {{ activeThemeObj.name }}
                </span>
                <span class="text-[10px] text-slate-500 dark:text-[#A6B4C8] block truncate">{{ activeThemeObj.tagline }}</span>
              </div>

              <!-- Quick Website Visibility Toggle -->
              <button
                type="button"
                @click="openToggleStatusModal"
                class="px-4 py-3 rounded-2xl text-xs font-bold inline-flex items-center justify-center gap-2 transition cursor-pointer shadow-xs active:scale-95"
                :class="websiteStatus === 'active'
                  ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/50 border border-rose-200 dark:border-rose-900/40'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-600/20'"
              >
                <PowerIcon class="w-4 h-4" />
                <span>{{ websiteStatus === 'active' ? 'Turn Off Website' : 'Turn On Website' }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 2. WEBSITE TEMPLATE SHOWCASE & INSTANT SWITCHER -->
        <div class="space-y-4">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div class="flex items-center gap-2 text-xs font-bold text-indigo-600 dark:text-blue-400 uppercase tracking-wider">
                <SparklesIcon class="w-4 h-4" />
                Visual Identity Engine
              </div>
              <h2 class="text-xl sm:text-2xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight">
                School Website Templates
              </h2>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8]">
                Choose from 6 production-ready school themes. Switching templates updates your entire public website immediately without losing any content.
              </p>
            </div>
            <button
              type="button"
              @click="navigateTo('theme-settings')"
              class="text-xs font-bold text-indigo-600 dark:text-blue-400 hover:text-indigo-800 dark:hover:text-blue-300 flex items-center gap-1 self-start sm:self-auto cursor-pointer"
            >
              <span>Advanced Color Customizer</span>
              <ArrowRightIcon class="w-3.5 h-3.5" />
            </button>
          </div>

          <!-- 6 Production Template Cards Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="tmpl in templateList"
              :key="tmpl.id"
              class="bg-white dark:bg-[#111C30] rounded-3xl border-2 transition-all duration-300 overflow-hidden flex flex-col justify-between hover:shadow-lg relative group"
              :class="currentTemplate === tmpl.id
                ? 'border-indigo-600 dark:border-blue-500 ring-4 ring-indigo-500/10 dark:ring-blue-500/10 shadow-md'
                : 'border-slate-200/90 dark:border-[#263449] hover:border-slate-300 dark:hover:border-slate-600'"
            >
              <!-- Top Preview Header Mockup -->
              <div
                class="p-4 border-b border-slate-100 dark:border-[#263449] relative overflow-hidden transition-all duration-300"
                :style="{ backgroundColor: tmpl.headerBg, color: tmpl.headerText }"
              >
                <!-- Mini Header Navigation Mockup -->
                <div class="flex items-center justify-between text-[10px] pb-3 border-b border-white/10 opacity-90">
                  <div class="flex items-center gap-1.5 font-bold truncate">
                    <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: tmpl.accentColor }"></span>
                    <span>{{ form.schoolName || 'School Name' }}</span>
                  </div>
                  <span class="font-mono text-[9px] uppercase tracking-wider" :style="{ color: tmpl.accentColor }">
                    {{ tmpl.code }}
                  </span>
                </div>

                <!-- Mini Hero Banner in Template Style -->
                <div class="pt-3 pb-2 space-y-1.5">
                  <span class="text-[9px] font-bold uppercase tracking-wider block opacity-75">
                    {{ tmpl.vibe }}
                  </span>
                  <div class="text-sm font-black truncate leading-tight">
                    {{ tmpl.name }}
                  </div>
                  <div class="flex items-center gap-2 pt-1">
                    <span
                      class="text-[9px] font-bold px-2 py-0.5 rounded-full inline-block font-sans shadow-xs"
                      :style="{ backgroundColor: tmpl.accentColor, color: tmpl.headerBg }"
                    >
                      Admissions Open
                    </span>
                    <span class="text-[10px] opacity-75 font-mono truncate">
                      {{ tmpl.fonts }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Template Description & Palette -->
              <div class="p-5 space-y-4 flex-1 flex flex-col justify-between">
                <div class="space-y-2">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-black text-slate-900 dark:text-[#F1F5F9]">{{ tmpl.name }}</h3>
                    <span
                      v-if="currentTemplate === tmpl.id"
                      class="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40 text-[10px] font-black uppercase flex items-center gap-1"
                    >
                      <CheckIcon class="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                      Active
                    </span>
                  </div>
                  <p class="text-xs text-slate-500 dark:text-[#A6B4C8] leading-relaxed min-h-[36px]">
                    {{ tmpl.description }}
                  </p>
                </div>

                <!-- 5 Color Palette Swatches -->
                <div class="space-y-1.5 pt-2 border-t border-slate-100 dark:border-[#263449]">
                  <span class="text-[10px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider block">Palette Swatches</span>
                  <div class="flex items-center gap-2">
                    <div
                      v-for="(color, cIdx) in tmpl.palette"
                      :key="cIdx"
                      class="group/swatch relative flex-1 h-6 rounded-lg border border-black/10 dark:border-white/10 shadow-xs cursor-pointer transition transform hover:scale-110"
                      :style="{ backgroundColor: color.hex }"
                      :title="`${color.label}: ${color.hex}`"
                    ></div>
                  </div>
                </div>

                <!-- Action Buttons: Apply vs Current vs Preview -->
                <div class="flex items-center gap-2 pt-2">
                  <button
                    type="button"
                    @click="openPreviewModal(tmpl)"
                    class="flex-1 py-2 px-3 rounded-xl border border-slate-200 dark:border-[#263449] bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#1F2E47] text-xs font-bold text-slate-700 dark:text-[#F1F5F9] transition cursor-pointer flex items-center justify-center gap-1"
                  >
                    <EyeIcon class="w-3.5 h-3.5 text-slate-500 dark:text-[#7E8DA5]" />
                    <span>Preview</span>
                  </button>

                  <button
                    v-if="currentTemplate === tmpl.id"
                    disabled
                    type="button"
                    class="flex-1 py-2 px-3 rounded-xl bg-emerald-600 text-white text-xs font-black flex items-center justify-center gap-1.5 opacity-90 cursor-default"
                  >
                    <CheckIcon class="w-3.5 h-3.5" />
                    <span>Active Now</span>
                  </button>

                  <button
                    v-else
                    type="button"
                    :disabled="isApplyingTemplate === tmpl.id"
                    @click="applyTemplate(tmpl.id)"
                    class="flex-1 py-2 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md shadow-indigo-600/20 transition cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <Loader2Icon v-if="isApplyingTemplate === tmpl.id" class="w-3.5 h-3.5 animate-spin" />
                    <SparklesIcon v-else class="w-3.5 h-3.5" />
                    <span>{{ isApplyingTemplate === tmpl.id ? 'Applying...' : 'Apply Theme' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. LAUNCH READINESS SCORECARD & MODULE AUDIT -->
        <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-xs p-6 sm:p-8 space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div class="flex items-center gap-2 text-xs font-bold text-indigo-600 dark:text-blue-400 uppercase tracking-wider">
                <CheckCircleIcon class="w-4 h-4" />
                Website Health Audit
              </div>
              <h2 class="text-xl sm:text-2xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight">
                Launch Readiness & Content Scorecard
              </h2>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8]">
                Ensure all essential content areas are populated to deliver an authoritative experience for parents and students.
              </p>
            </div>

            <!-- Readiness Percentage Badge -->
            <div class="flex items-center gap-3 bg-slate-50 dark:bg-[#172235] px-4 py-2.5 rounded-2xl border border-slate-200/80 dark:border-[#263449] self-start sm:self-auto">
              <div class="text-right">
                <span class="text-xl font-black text-slate-900 dark:text-[#F1F5F9] font-mono">{{ readinessScore }}%</span>
                <span class="text-[10px] text-slate-500 dark:text-[#7E8DA5] block uppercase font-bold">Portal Score</span>
              </div>
              <div class="w-10 h-10 rounded-full flex items-center justify-center bg-indigo-100 dark:bg-blue-950/60 text-indigo-700 dark:text-blue-400 font-black text-xs">
                {{ readinessScore >= 80 ? '🌟' : '⚡' }}
              </div>
            </div>
          </div>

          <!-- Progress Bar -->
          <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2.5 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="readinessScore >= 80 ? 'bg-emerald-500' : readinessScore >= 50 ? 'bg-indigo-600 dark:bg-blue-500' : 'bg-amber-500'"
              :style="{ width: `${readinessScore}%` }"
            ></div>
          </div>

          <!-- Audit Checklist Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
            <div
              v-for="item in readinessItems"
              :key="item.id"
              class="p-4 rounded-2xl border transition flex items-center justify-between gap-3"
              :class="item.isComplete
                ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-200/70 dark:border-emerald-800/40'
                : 'bg-slate-50/60 dark:bg-[#18243A]/50 border-slate-200/70 dark:border-[#263449]'"
            >
              <div class="space-y-0.5">
                <div class="flex items-center gap-2">
                  <CheckCircleIcon v-if="item.isComplete" class="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <AlertCircleIcon v-else class="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <span class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9]">{{ item.label }}</span>
                </div>
                <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8] pl-6">{{ item.desc }}</p>
              </div>

              <button
                type="button"
                @click="item.type === 'internal' ? (activeStudioTab = item.targetTab) : navigateTo(item.targetTab)"
                class="px-2.5 py-1 text-[11px] font-bold rounded-lg border transition flex-shrink-0 cursor-pointer"
                :class="item.isComplete
                  ? 'border-emerald-300 dark:border-emerald-700/50 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/50'
                  : 'border-slate-300 dark:border-[#263449] text-slate-700 dark:text-[#F1F5F9] bg-white dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#1F2E47]'"
              >
                {{ item.isComplete ? 'Edit' : 'Configure' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 4. QUICK CMS MANAGEMENT LAUNCHPAD -->
        <div class="space-y-4">
          <div>
            <h2 class="text-lg sm:text-xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight">
              Website CMS Sections
            </h2>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
              Quickly jump to any section editor to update live school content.
            </p>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-4">
            <div
              v-for="mod in cmsModules"
              :key="mod.id"
              @click="mod.type === 'internal' ? (activeStudioTab = mod.target) : navigateTo(mod.target)"
              class="bg-white dark:bg-[#111C30] p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] hover:border-indigo-400 dark:hover:border-blue-500 hover:shadow-md transition-all cursor-pointer group space-y-2.5"
            >
              <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-slate-50 dark:bg-[#18243A] group-hover:bg-indigo-50 dark:group-hover:bg-blue-950/50 text-slate-700 dark:text-[#F1F5F9] group-hover:text-indigo-600 dark:group-hover:text-blue-400 flex items-center justify-center transition">
                <component :is="mod.icon" class="w-5 h-5" />
              </div>
              <div>
                <h4 class="text-xs sm:text-sm font-bold text-slate-900 dark:text-[#F1F5F9] group-hover:text-indigo-600 dark:group-hover:text-blue-400 transition">
                  {{ mod.title }}
                </h4>
                <p class="text-[11px] text-slate-400 dark:text-[#7E8DA5] line-clamp-1">
                  {{ mod.desc }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- VIEW 2: HEADER BRANDING & NAVIGATION STUDIO (CANVAS) -->
      <!-- ======================================================== -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <!-- LEFT COLUMN: STICKY REAL-TIME PREVIEW CANVAS (5 Cols on LG) -->
        <div class="lg:col-span-5 lg:sticky lg:top-6 space-y-4">
          <div class="flex items-center justify-between px-1">
            <span class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-[#A6B4C8] flex items-center gap-2">
              <EyeIcon class="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              Live Canvas Preview
            </span>
            <span class="bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-400 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800/40">
              Synced Real-time
            </span>
          </div>

          <!-- The Rendered Header Mockup -->
          <div class="rounded-3xl border border-slate-300/80 dark:border-[#263449] bg-white dark:bg-[#111C30] shadow-xl shadow-slate-900/5 overflow-hidden transition-all duration-300 ring-4 ring-slate-900/5">
            <!-- 1. Dark Top Bar -->
            <div class="bg-[#0f2423] text-[#e2e8f0] text-[11px] py-2.5 px-4 flex items-center justify-between gap-2 border-b border-[#1b3d3a]">
              <div class="flex items-center gap-3 truncate font-medium">
                <span v-if="form.city || form.address" class="flex items-center gap-1 shrink-0">
                  <MapPinIcon class="h-3 w-3 text-[#d4af37]" />
                  {{ form.city || form.address }}
                </span>
                <span v-if="form.primaryPhone" class="flex items-center gap-1 shrink-0">
                  <PhoneIcon class="h-3 w-3 text-[#d4af37]" />
                  {{ form.primaryPhone }}
                </span>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <a v-if="form.facebook" :href="form.facebook" target="_blank" class="text-slate-400 hover:text-white transition">
                  <FacebookIcon class="h-3 w-3" />
                </a>
                <a v-if="form.youtube" :href="form.youtube" target="_blank" class="text-slate-400 hover:text-white transition">
                  <YoutubeIcon class="h-3 w-3" />
                </a>
              </div>
            </div>

            <!-- 2. Main Branding Bar -->
            <div class="p-4 bg-white dark:bg-[#111C30] flex items-center justify-between gap-3 border-b border-slate-100 dark:border-[#263449]">
              <div class="flex items-center gap-3 min-w-0">
                <!-- Logo Avatar -->
                <div class="h-12 w-12 rounded-xl bg-gradient-to-tr from-slate-900 to-slate-800 flex items-center justify-center text-white font-extrabold text-sm shrink-0 shadow-sm border border-slate-200 dark:border-[#263449] overflow-hidden">
                  <img
                    v-if="resolvedLogoUrl"
                    :src="resolvedLogoUrl"
                    alt="Logo"
                    class="h-full w-full object-contain p-1"
                  />
                  <span v-else>{{ schoolInitials }}</span>
                </div>
                <!-- Titles -->
                <div class="min-w-0">
                  <h3 class="text-sm font-extrabold text-slate-900 dark:text-[#F1F5F9] truncate leading-snug">
                    {{ form.schoolName || 'School Name' }}
                  </h3>
                  <p class="text-[10px] text-slate-500 dark:text-[#A6B4C8] truncate">
                    {{ form.tagline || form.boardAffiliation || 'Affiliated to CBSE' }}
                  </p>
                </div>
              </div>

              <!-- Action buttons -->
              <div class="flex items-center gap-1.5 shrink-0">
                <span class="hidden sm:inline-block px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-400 text-[10px] font-bold border border-emerald-200 dark:border-emerald-800/40">
                  Apply Online
                </span>
                <span class="px-2.5 py-1 rounded-lg bg-slate-900 dark:bg-[#3B82F6] text-white text-[10px] font-bold">
                  Login
                </span>
              </div>
            </div>

            <!-- 3. Navigation Bar Mockup -->
            <div class="bg-slate-50 dark:bg-[#172235] px-4 py-2 flex items-center gap-2 overflow-x-auto border-b border-slate-200/80 dark:border-[#263449]">
              <div class="flex items-center gap-1 text-[11px] font-bold text-slate-700 dark:text-[#F1F5F9] whitespace-nowrap">
                <span
                  v-for="(item, idx) in activeNavItems"
                  :key="idx"
                  class="px-2.5 py-1 rounded-md transition"
                  :class="idx === 0 ? 'bg-white dark:bg-[#111C30] text-emerald-700 dark:text-emerald-400 shadow-xs' : 'text-slate-600 dark:text-[#A6B4C8]'"
                >
                  {{ item.label }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN: STUDIO CONTROLS (7 Cols on LG) -->
        <div class="lg:col-span-7 space-y-6">
          <!-- Inner Sub-Tabs: Header Branding vs Navigation Items -->
          <div class="flex items-center gap-2 border-b border-slate-200 dark:border-[#263449] pb-2">
            <button
              type="button"
              @click="activeHeaderTab = 'branding'"
              class="px-4 py-2 text-xs font-bold rounded-xl transition cursor-pointer"
              :class="activeHeaderTab === 'branding'
                ? 'bg-slate-900 dark:bg-[#3B82F6] text-white shadow-xs'
                : 'bg-white dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] border border-slate-200 dark:border-[#263449]'"
            >
              Header Branding & Contact
            </button>
            <button
              type="button"
              @click="activeHeaderTab = 'navigation'"
              class="px-4 py-2 text-xs font-bold rounded-xl transition cursor-pointer"
              :class="activeHeaderTab === 'navigation'
                ? 'bg-slate-900 dark:bg-[#3B82F6] text-white shadow-xs'
                : 'bg-white dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] border border-slate-200 dark:border-[#263449]'"
            >
              Navigation Menu Items ({{ form.navItems?.length || 0 }})
            </button>
          </div>

          <!-- TAB A: BRANDING FIELDS -->
          <div v-if="activeHeaderTab === 'branding'" class="bg-white dark:bg-[#111C30] p-6 rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5">
            <div>
              <h3 class="text-sm font-extrabold text-slate-900 dark:text-[#F1F5F9]">Branding & School Identity</h3>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">Configure your logo, official titles, and header contact information.</p>
            </div>

            <!-- Logo Upload -->
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">School Header Logo</label>
              <ImageUploader v-model="form.logo" />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Official School Name *</label>
                <input
                  v-model="form.schoolName"
                  type="text"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="e.g. Vinoda Academy"
                  required
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Tagline / Motto</label>
                <input
                  v-model="form.tagline"
                  type="text"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="e.g. Excellence in Education"
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Board Affiliation</label>
                <input
                  v-model="form.boardAffiliation"
                  type="text"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="e.g. CBSE Affiliated"
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Affiliation Number</label>
                <input
                  v-model="form.affiliationNumber"
                  type="text"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="e.g. 230045"
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Primary Phone</label>
                <input
                  v-model="form.primaryPhone"
                  type="text"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="+91..."
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Official Email</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="info@school.com"
                />
              </div>

              <div class="space-y-1.5 sm:col-span-2">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Campus Address / Location</label>
                <input
                  v-model="form.address"
                  type="text"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="e.g. Tezpur, Assam, India"
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">Facebook Page URL</label>
                <input
                  v-model="form.facebook"
                  type="url"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="https://facebook.com/..."
                />
              </div>

              <div class="space-y-1.5">
                <label class="text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">YouTube Channel URL</label>
                <input
                  v-model="form.youtube"
                  type="url"
                  class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="https://youtube.com/..."
                />
              </div>
            </div>
          </div>

          <!-- TAB B: NAVIGATION ITEMS EDITOR -->
          <div v-else class="bg-white dark:bg-[#111C30] p-6 rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-extrabold text-slate-900 dark:text-[#F1F5F9]">Header Navigation Menu</h3>
                <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">Order, enable or add menu links to your public website header.</p>
              </div>
              <button
                type="button"
                @click="addNavItem"
                class="px-3 py-1.5 bg-indigo-50 dark:bg-blue-950/60 hover:bg-indigo-100 dark:hover:bg-blue-900/50 text-indigo-700 dark:text-blue-400 text-xs font-bold rounded-xl border border-indigo-200 dark:border-blue-800/40 transition flex items-center gap-1 cursor-pointer"
              >
                <PlusIcon class="h-3.5 w-3.5" />
                <span>Add Item</span>
              </button>
            </div>

            <!-- List of Nav Items -->
            <div class="space-y-3 max-h-[500px] overflow-y-auto pr-1">
              <div
                v-for="(item, index) in form.navItems"
                :key="index"
                class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3.5 rounded-2xl border transition-all"
                :class="item.enabled !== false ? 'border-slate-200 dark:border-[#263449] bg-slate-50/60 dark:bg-[#18243A]/50' : 'border-slate-200/60 dark:border-[#263449]/60 bg-slate-100/60 dark:bg-[#172235]/60 opacity-60'"
              >
                <div class="flex items-center justify-between sm:justify-start gap-3">
                  <span class="text-xs font-mono font-bold text-slate-400 dark:text-[#7E8DA5]">#{{ index + 1 }}</span>
                  <button
                    type="button"
                    class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="item.enabled !== false ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'"
                    @click="item.enabled = item.enabled === false"
                  >
                    <span
                      class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out"
                      :class="item.enabled !== false ? 'translate-x-5' : 'translate-x-0'"
                    />
                  </button>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
                  <input
                    v-model="item.label"
                    type="text"
                    placeholder="Menu Label"
                    class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#172235] px-3 py-1.5 text-xs font-bold text-slate-800 dark:text-[#F1F5F9] focus:border-indigo-500 focus:outline-none"
                  />
                  <input
                    v-model="item.url"
                    type="text"
                    placeholder="Target Link / Anchor (#)"
                    class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#172235] px-3 py-1.5 text-xs text-slate-700 dark:text-[#F1F5F9] focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div class="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    :disabled="index === 0"
                    class="p-1.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-500 dark:text-[#7E8DA5] hover:text-indigo-600 dark:hover:text-blue-400 disabled:opacity-30 cursor-pointer transition active:scale-95"
                    @click="moveNavItem(index, -1)"
                  >
                    <ArrowUpIcon class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    :disabled="index === form.navItems.length - 1"
                    class="p-1.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-500 dark:text-[#7E8DA5] hover:text-indigo-600 dark:hover:text-blue-400 disabled:opacity-30 cursor-pointer transition active:scale-95"
                    @click="moveNavItem(index, 1)"
                  >
                    <ArrowDownIcon class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer transition active:scale-95"
                    @click="removeNavItem(index)"
                  >
                    <Trash2Icon class="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Floating Save Bar for Header Studio (Sticky to bottom right always) -->
          <teleport to="body">
            <div
              v-if="activeStudioTab === 'header'"
              class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-lg pointer-events-auto"
            >
              <div
                class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 sm:gap-4 ring-1 ring-slate-950/5 dark:ring-white/5"
              >
                <!-- Status Indicator -->
                <div class="flex items-center gap-2 text-xs font-medium min-w-0">
                  <span
                    v-if="isSaving"
                    class="inline-flex items-center gap-1.5 text-indigo-600 dark:text-blue-400 font-semibold truncate"
                  >
                    <Loader2Icon class="h-4 w-4 animate-spin shrink-0 text-indigo-600 dark:text-blue-400" />
                    <span class="truncate">Saving...</span>
                  </span>
                  <span
                    v-else-if="hasUnsavedChanges"
                    class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/50 px-2.5 py-1 border border-amber-200/80 dark:border-amber-800/40 text-amber-700 dark:text-amber-400 font-bold shrink-0"
                  >
                    <span class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"></span>
                    <span>Unsaved</span>
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center gap-1.5 text-slate-500 dark:text-[#A6B4C8] truncate"
                  >
                    <CheckCircleIcon class="h-4 w-4 text-emerald-500 shrink-0" />
                    <span class="hidden sm:inline text-xs truncate">All changes saved</span>
                  </span>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer"
                    :disabled="isSaving"
                    @click="confirmReset"
                  >
                    Discard
                  </button>

                  <button
                    type="button"
                    class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-4 sm:px-5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                    :disabled="isSaving"
                    @click="handleSave"
                  >
                    <Loader2Icon v-if="isSaving" class="h-3.5 w-3.5 animate-spin" />
                    <SaveIcon v-else class="h-3.5 w-3.5" />
                    <span class="whitespace-nowrap">{{ isSaving ? "Saving..." : "Save Configuration" }}</span>
                  </button>
                </div>
              </div>
            </div>
          </teleport>
        </div>
      </div>
    </div>

    <!-- TEMPLATE PREVIEW MODAL -->
    <div
      v-if="showPreviewModal && previewTargetTemplate"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 dark:bg-black/80 p-4 backdrop-blur-sm"
      @click.self="showPreviewModal = false"
    >
      <div class="w-full max-w-4xl bg-white dark:bg-[#111C30] rounded-3xl shadow-2xl overflow-hidden border border-slate-200 dark:border-[#263449] flex flex-col max-h-[90vh]">
        <!-- Modal Top Header -->
        <div class="p-4 sm:p-6 bg-slate-900 dark:bg-[#0F172A] text-white flex items-center justify-between border-b border-slate-800 dark:border-[#263449]">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-white/10 dark:bg-[#18243A] flex items-center justify-center text-white">
              <PaletteIcon class="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 class="text-base font-black text-white dark:text-[#F1F5F9]">{{ previewTargetTemplate.name }}</h3>
              <p class="text-xs text-slate-400 dark:text-[#A6B4C8]">{{ previewTargetTemplate.tagline }} · {{ previewTargetTemplate.vibe }}</p>
            </div>
          </div>
          <button
            @click="showPreviewModal = false"
            class="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white flex items-center justify-center transition cursor-pointer"
          >
            <XIcon class="w-4 h-4" />
          </button>
        </div>

        <!-- Rendered Full Template Mockup -->
        <div class="p-6 overflow-y-auto space-y-6 flex-1 bg-[#faf8f5] dark:bg-[#0B1120]">
          <!-- Template Bar Mockup -->
          <div
            class="rounded-2xl p-5 shadow-sm border border-black/10 dark:border-white/10"
            :style="{ backgroundColor: previewTargetTemplate.headerBg, color: previewTargetTemplate.headerText }"
          >
            <div class="flex items-center justify-between pb-3 border-b border-white/15">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center font-bold text-xs" :style="{ color: previewTargetTemplate.accentColor }">
                  {{ schoolInitials }}
                </div>
                <span class="font-extrabold text-sm">{{ form.schoolName || 'School Name' }}</span>
              </div>
              <span class="text-xs font-mono px-2 py-0.5 rounded" :style="{ backgroundColor: 'rgba(255,255,255,0.1)', color: previewTargetTemplate.accentColor }">
                {{ previewTargetTemplate.code }}
              </span>
            </div>

            <!-- Hero Banner Mockup in this template -->
            <div class="pt-6 pb-4 space-y-3">
              <span class="text-[10px] font-bold tracking-widest uppercase" :style="{ color: previewTargetTemplate.accentColor }">
                EXCELLENCE IN EDUCATION · 2026
              </span>
              <h1 class="text-2xl sm:text-3xl font-black leading-tight max-w-xl">
                Nurturing Tomorrow's Visionaries & Global Leaders
              </h1>
              <p class="text-xs sm:text-sm opacity-85 max-w-lg leading-relaxed">
                {{ form.tagline || 'A curriculum designed to cultivate intellectual curiosity, emotional resilience, and lifelong character.' }}
              </p>
              <div class="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  class="px-4 py-2 rounded-lg text-xs font-bold"
                  :style="{ backgroundColor: previewTargetTemplate.accentColor, color: previewTargetTemplate.headerBg }"
                >
                  Apply for Admission
                </button>
                <button
                  type="button"
                  class="px-4 py-2 rounded-lg text-xs font-bold border border-white/30 text-white"
                >
                  Explore Curriculum
                </button>
              </div>
            </div>
          </div>

          <!-- Color Swatch Breakdown -->
          <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] space-y-3">
            <h4 class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] uppercase tracking-wider">Template Palette Specification</h4>
            <div class="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div
                v-for="(color, cIdx) in previewTargetTemplate.palette"
                :key="cIdx"
                class="p-2.5 rounded-xl border border-slate-100 dark:border-[#263449] bg-slate-50 dark:bg-[#18243A] space-y-1.5"
              >
                <div class="h-8 rounded-lg border border-black/10 dark:border-white/10 shadow-xs" :style="{ backgroundColor: color.hex }"></div>
                <div>
                  <span class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] block truncate">{{ color.label }}</span>
                  <span class="text-[10px] font-mono text-slate-500 dark:text-[#A6B4C8] uppercase">{{ color.hex }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer Actions -->
        <div class="p-4 bg-slate-50 dark:bg-[#0F172A] border-t border-slate-200 dark:border-[#263449] flex items-center justify-end gap-3">
          <button
            type="button"
            @click="showPreviewModal = false"
            class="px-4 py-2 text-xs font-bold text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] transition"
          >
            Close Preview
          </button>
          <button
            type="button"
            @click="applyTemplateFromModal"
            :disabled="isApplyingTemplate === previewTargetTemplate.id || currentTemplate === previewTargetTemplate.id"
            class="px-5 py-2.5 rounded-xl text-xs font-bold text-white transition flex items-center gap-1.5 shadow-md"
            :class="currentTemplate === previewTargetTemplate.id
              ? 'bg-emerald-600 opacity-90 cursor-default'
              : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/25 cursor-pointer'"
          >
            <CheckIcon v-if="currentTemplate === previewTargetTemplate.id" class="w-4 h-4" />
            <Loader2Icon v-else-if="isApplyingTemplate === previewTargetTemplate.id" class="w-4 h-4 animate-spin" />
            <SparklesIcon v-else class="w-4 h-4" />
            <span>{{ currentTemplate === previewTargetTemplate.id ? 'Already Active' : 'Activate This Template Now' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- TOGGLE WEBSITE STATUS CONFIRMATION MODAL -->
    <div
      v-if="showStatusModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 dark:bg-black/80 p-4 backdrop-blur-sm"
      @click.self="showStatusModal = false"
    >
      <div class="w-full max-w-md bg-white dark:bg-[#111C30] rounded-3xl p-6 shadow-2xl border-t-4 border-slate-200 dark:border-[#263449]"
        :class="websiteStatus === 'active' ? 'border-t-rose-600' : 'border-t-emerald-600'"
      >
        <h3 class="text-lg font-bold text-slate-900 dark:text-[#F1F5F9]">
          {{ websiteStatus === 'active' ? 'Turn Off Live Website?' : 'Turn Website Online?' }}
        </h3>
        <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] mt-2 leading-relaxed">
          {{ websiteStatus === 'active'
            ? 'Visitors to your school portal will temporarily see a maintenance screen. You can turn it back on anytime.'
            : 'Your public website and admissions pages will become live and accessible to the public internet.' }}
        </p>
        <div class="flex justify-end gap-3 mt-6">
          <button
            type="button"
            @click="showStatusModal = false"
            class="px-4 py-2 text-xs font-bold text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-xl cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="executeToggleStatus"
            :disabled="isTogglingStatus"
            class="px-5 py-2 text-xs font-bold text-white rounded-xl shadow-md transition active:scale-95 flex items-center gap-1.5 cursor-pointer"
            :class="websiteStatus === 'active' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'"
          >
            <Loader2Icon v-if="isTogglingStatus" class="w-3.5 h-3.5 animate-spin" />
            <span>Confirm</span>
          </button>
        </div>
      </div>
    </div>

    <!-- DISCARD RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 dark:bg-black/80 p-4 backdrop-blur-sm"
      @click.self="showResetModal = false"
    >
      <div class="w-full max-w-md space-y-4 overflow-hidden rounded-3xl bg-white dark:bg-[#111C30] p-6 shadow-2xl border border-slate-200 dark:border-[#263449]">
        <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9]">Discard Unsaved Changes?</h3>
        <p class="text-xs text-slate-500 dark:text-[#A6B4C8] leading-relaxed">
          Are you sure you want to reset all fields back to their last saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 dark:border-[#263449] px-4 py-2 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#18243A] active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-6 right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon v-if="toast.type === 'success'" class="h-4 w-4" />
        <AlertCircleIcon v-else class="h-4 w-4" />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, inject, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    ArrowUp as ArrowUpIcon,
    ArrowDown as ArrowDownIcon,
    ArrowRight as ArrowRightIcon,
    MapPin as MapPinIcon,
    Phone as PhoneIcon,
    Facebook as FacebookIcon,
    Youtube as YoutubeIcon,
    Eye as EyeIcon,
    Globe as GlobeIcon,
    ExternalLink as ExternalLinkIcon,
    Copy as CopyIcon,
    Palette as PaletteIcon,
    Sparkles as SparklesIcon,
    Check as CheckIcon,
    ShieldCheck as ShieldCheckIcon,
    LayoutDashboard as LayoutDashboardIcon,
    Power as PowerIcon,
    X as XIcon,
    School as SchoolIcon,
    Images as ImagesIcon,
    Users as UsersIcon,
    GraduationCap as GraduationCapIcon,
    Building2 as Building2Icon,
    Newspaper as NewspaperIcon,
    CalendarDays as CalendarDaysIcon,
    Settings as SettingsIcon,
  } from "lucide-vue-next";

  import ImageUploader from "@/components/WEB/ImageUploader.vue";

  const store = useStore();
  const changePage = inject("changePage", () => {});

  // Studio View Mode: 'overview' | 'header'
  const activeStudioTab = ref("overview");

  // Inner subtab on header studio: 'branding' | 'navigation'
  const activeHeaderTab = ref("branding");

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);
  const showStatusModal = ref(false);
  const isTogglingStatus = ref(false);

  // Template preview modal state
  const showPreviewModal = ref(false);
  const previewTargetTemplate = ref(null);
  const isApplyingTemplate = ref("");

  const defaultNavItems = [
    { label: "Home", url: "#", enabled: true, sortOrder: 0 },
    { label: "About", url: "#about", enabled: true, sortOrder: 1 },
    { label: "Academics", url: "#academics", enabled: true, sortOrder: 2 },
    { label: "Admissions", url: "#admissions", enabled: true, sortOrder: 3 },
    { label: "Facilities", url: "#facilities", enabled: true, sortOrder: 4 },
  ];

  const form = reactive({
    logo: "",
    schoolName: "Vinoda Academy",
    tagline: "Join Educanium International School",
    establishmentYear: "2000",
    boardAffiliation: "CBSE Affiliated",
    affiliationNumber: "230045",
    officeHours: "Mon–Sat · 8:00 AM–3:00 PM",
    primaryPhone: "+918638803228",
    email: "info@school.com",
    city: "Tezpur",
    state: "Assam",
    address: "",
    facebook: "https://facebook.com",
    youtube: "https://youtube.com",
    portalLink: "https://portal.school.com",
    admissionLink: "https://school.nexterp.in/admission",
    navItems: JSON.parse(JSON.stringify(defaultNavItems)),
  });

  const initialStateJson = ref("");

  /* ========================================================
     6 OFFICIAL PRODUCTION TEMPLATES (MATCHING website.ejs)
  ======================================================== */
  const templateList = [
    {
      id: "default",
      code: "classic",
      name: "The School Diary",
      tagline: "Classic Academic Heritage",
      description:
        "A nostalgic, exercise-book inspired design featuring deep navy ink, crisp cream parchment, and warm gold leaf stamps.",
      vibe: "Timeless & Authoritative",
      fonts: "Fraunces · Instrument Serif · Plus Jakarta",
      palette: [
        { label: "Ink Navy", hex: "#11151F" },
        { label: "Cream Paper", hex: "#FAF7F1" },
        { label: "Margin Rule", hex: "#1F3A5F" },
        { label: "Wine Red", hex: "#7A1F2E" },
        { label: "Brass Gold", hex: "#B8863B" },
      ],
      headerBg: "#11151F",
      headerText: "#FAF7F1",
      accentColor: "#B8863B",
      cardBg: "#F1EADC",
    },
    {
      id: "template1",
      code: "eco",
      name: "Botanical Campus",
      tagline: "Nature, Eco & Growth",
      description:
        "A fresh, natural identity with forest emerald tones, leafy rule lines, mint canvas, and terracotta margin details.",
      vibe: "Fresh & Environmental",
      fonts: "Fraunces · Instrument Serif · Plus Jakarta",
      palette: [
        { label: "Forest Green", hex: "#0C1F17" },
        { label: "Mint Paper", hex: "#F8F6EE" },
        { label: "Leaf Green", hex: "#1D5C3E" },
        { label: "Terracotta", hex: "#7A4A1F" },
        { label: "Amber Gold", hex: "#C79A46" },
      ],
      headerBg: "#0C1F17",
      headerText: "#F8F6EE",
      accentColor: "#C79A46",
      cardBg: "#EEEADA",
    },
    {
      id: "template2",
      code: "royal",
      name: "Royal Academy",
      tagline: "Regal Prestige & Distinction",
      description:
        "Regal and distinguished style driven by deep plum midnight, blush ivory paper, royal crimson, and champagne gold trim.",
      vibe: "Collegiate & Noble",
      fonts: "Fraunces · Instrument Serif · Plus Jakarta",
      palette: [
        { label: "Plum Midnight", hex: "#190F2C" },
        { label: "Blush Ivory", hex: "#FAF7FB" },
        { label: "Imperial Violet", hex: "#4B2E83" },
        { label: "Royal Crimson", hex: "#7A1F44" },
        { label: "Champagne Gold", hex: "#C9A15A" },
      ],
      headerBg: "#190F2C",
      headerText: "#FAF7FB",
      accentColor: "#C9A15A",
      cardBg: "#EFE7F5",
    },
    {
      id: "template3",
      code: "stem",
      name: "Modern STEM",
      tagline: "Innovation, Tech & Robotics",
      description:
        "High-tech slate and cobalt design engineered for modern science, technology, mathematics, and future-ready institutions.",
      vibe: "Modern & Engineering",
      fonts: "Fraunces · Space Mono · Plus Jakarta",
      palette: [
        { label: "Slate Navy", hex: "#0A1830" },
        { label: "Digital White", hex: "#F7F8FA" },
        { label: "Deep Cobalt", hex: "#1E3A67" },
        { label: "Warm Bronze", hex: "#8A5A20" },
        { label: "Digital Amber", hex: "#C2954A" },
      ],
      headerBg: "#0A1830",
      headerText: "#F7F8FA",
      accentColor: "#C2954A",
      cardBg: "#EAEDF3",
    },
    {
      id: "template4",
      code: "earth",
      name: "Sunset Terracotta",
      tagline: "Warm Earth, Heritage & Craft",
      description:
        "Warm earthy tones featuring espresso ink, textured sand parchment, terracotta margins, and sage green elements.",
      vibe: "Warm & Artistic",
      fonts: "Fraunces · Instrument Serif · Plus Jakarta",
      palette: [
        { label: "Espresso", hex: "#2A1A10" },
        { label: "Sand Paper", hex: "#FBF7F1" },
        { label: "Terracotta Rust", hex: "#7A3D18" },
        { label: "Sage Green", hex: "#2F4F3D" },
        { label: "Warm Gold", hex: "#C79447" },
      ],
      headerBg: "#2A1A10",
      headerText: "#FBF7F1",
      accentColor: "#C79447",
      cardBg: "#F0E5D6",
    },
    {
      id: "template5",
      code: "nordic",
      name: "Nordic Slate & Amber",
      tagline: "Minimalist Scandinavian Modern",
      description:
        "Clean Scandinavian minimalism featuring dark slate, cool ocean mist canvas, deep pine teal, and bright amber accents.",
      vibe: "Minimal & Nordic",
      fonts: "Fraunces · Space Mono · Plus Jakarta",
      palette: [
        { label: "Dark Slate", hex: "#0D2430" },
        { label: "Cool Grey", hex: "#F6F9F8" },
        { label: "Nordic Teal", hex: "#155E56" },
        { label: "Amber Glow", hex: "#8A5A20" },
        { label: "Bright Amber", hex: "#C08A3E" },
      ],
      headerBg: "#0D2430",
      headerText: "#F6F9F8",
      accentColor: "#C08A3E",
      cardBg: "#E9F1EF",
    },
  ];

  /* ========================================================
     CMS SECTIONS LAUNCHPAD MODULES
  ======================================================== */
  const cmsModules = [
    { id: "hero", title: "Hero Banners", desc: "Slider images, highlights & stats", target: "hero-section", type: "nav", icon: ImagesIcon },
    { id: "about", title: "About School", desc: "History, vision & leadership story", target: "about-school", type: "nav", icon: SchoolIcon },
    { id: "principal", title: "Principal Desk", desc: "Official welcome message & desk note", target: "leadership", type: "nav", icon: UsersIcon },
    { id: "academics", title: "Academics", desc: "Curriculum streams & learning stages", target: "academics", type: "nav", icon: GraduationCapIcon },
    { id: "facilities", title: "Campus & Labs", desc: "Interactive infrastructure showcases", target: "facilities", type: "nav", icon: Building2Icon },
    { id: "notices", title: "Notice Board", desc: "Public announcements & circulars", target: "notifications", type: "nav", icon: NewspaperIcon },
    { id: "events", title: "Calendar Events", desc: "Upcoming sports & cultural dates", target: "events", type: "nav", icon: CalendarDaysIcon },
    { id: "settings", title: "Domain & SEO", desc: "Custom domain, DNS & status", target: "website-settings", type: "nav", icon: SettingsIcon },
  ];

  /* ========================================================
     COMPUTED PROPERTIES
  ======================================================== */
  const schoolDetails = computed(() => store.state.websiteDetails || {});

  const currentTemplate = computed(() => {
    return (schoolDetails.value?.template || "default").toLowerCase().trim();
  });

  const activeThemeObj = computed(() => {
    return templateList.find((t) => t.id === currentTemplate.value) || templateList[0];
  });

  const websiteStatus = computed(() => {
    return schoolDetails.value?.websiteStatus || "active";
  });

  const isLocalDev = computed(() => {
    if (typeof window === "undefined") return false;
    const h = window.location.hostname.toLowerCase();
    return h === "localhost" || h === "127.0.0.1" || h.endsWith(".localhost");
  });

  const displayDomain = computed(() => {
    if (schoolDetails.value?.customDomain) return schoolDetails.value.customDomain;
    if (schoolDetails.value?.slug) return `${schoolDetails.value.slug}.educanium.com`;
    return "school.educanium.com";
  });

  const liveProductionUrl = computed(() => {
    if (schoolDetails.value?.customDomain) return `https://${schoolDetails.value.customDomain}`;
    if (schoolDetails.value?.slug) return `https://${schoolDetails.value.slug}.educanium.com`;
    return "https://school.educanium.com";
  });

  const fullPublicUrl = computed(() => {
    if (schoolDetails.value?.customDomain) return `https://${schoolDetails.value.customDomain}`;
    const slug = schoolDetails.value?.slug || "school";
    if (isLocalDev.value) {
      const port = window.location.port ? `:${window.location.port}` : "";
      return `${window.location.protocol}//${window.location.hostname}${port}/site/${slug}`;
    }
    return `https://${slug}.educanium.com`;
  });

  const activeNavItems = computed(() => {
    return (form.navItems || []).filter((item) => item.enabled !== false);
  });

  const schoolInitials = computed(() => {
    if (!form.schoolName) return "VA";
    return form.schoolName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  });

  const resolvedLogoUrl = computed(() => {
    return resolvedImageUrl(form.logo);
  });

  function resolvedImageUrl(media) {
    if (!media) return "";
    if (typeof media === "string") return media;
    if (media instanceof File) return URL.createObjectURL(media);
    return media.url || media.secure_url || "";
  }

  // Health / Readiness Scorecard Items
  const readinessItems = computed(() => {
    const d = schoolDetails.value || {};
    return [
      {
        id: "header",
        label: "Header & Branding",
        desc: "School name, logo, phone & email",
        isComplete: Boolean(form.schoolName && form.primaryPhone && form.email),
        targetTab: "header",
        type: "internal",
      },
      {
        id: "template",
        label: "Visual Theme",
        desc: `Active identity: ${activeThemeObj.value.name}`,
        isComplete: Boolean(currentTemplate.value),
        targetTab: "theme-settings",
        type: "nav",
      },
      {
        id: "hero",
        label: "Hero Sliders",
        desc: "Homepage hero slides and badges",
        isComplete: Boolean(Array.isArray(d.heroSlides) && d.heroSlides.length > 0),
        targetTab: "hero-section",
        type: "nav",
      },
      {
        id: "about",
        label: "About School",
        desc: "Vision, mission & campus background",
        isComplete: Boolean(d.aboutSchool || d.description),
        targetTab: "about-school",
        type: "nav",
      },
      {
        id: "leadership",
        label: "Principal Desk",
        desc: "Principal desk message and photo",
        isComplete: Boolean(d.principalName || d.principalMessage),
        targetTab: "leadership",
        type: "nav",
      },
      {
        id: "academics",
        label: "Academics",
        desc: "Curriculum programs and subjects",
        isComplete: Boolean(Array.isArray(d.academicPrograms) && d.academicPrograms.length > 0),
        targetTab: "academics",
        type: "nav",
      },
      {
        id: "facilities",
        label: "Facilities & Labs",
        desc: "Infrastructure & campus highlights",
        isComplete: Boolean(Array.isArray(d.facilities) && d.facilities.length > 0),
        targetTab: "facilities",
        type: "nav",
      },
      {
        id: "notifications",
        label: "Notice Board",
        desc: "Public circulars & announcement cards",
        isComplete: Boolean(Array.isArray(d.news) && d.news.length > 0),
        targetTab: "notifications",
        type: "nav",
      },
      {
        id: "domain",
        label: "Domain & SSL",
        desc: "Connected domain & active certificate",
        isComplete: Boolean(d.slug || d.customDomain),
        targetTab: "website-settings",
        type: "nav",
      },
    ];
  });

  const readinessScore = computed(() => {
    const items = readinessItems.value;
    const completed = items.filter((i) => i.isComplete).length;
    return Math.round((completed / items.length) * 100);
  });

  /* ========================================================
     TOAST STATE
  ======================================================== */
  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  /* ========================================================
     ACTIONS & METHODS
  ======================================================== */
  const copyPublicUrl = async () => {
    try {
      await navigator.clipboard.writeText(liveProductionUrl.value || fullPublicUrl.value);
      showToast("Portal link copied to clipboard!", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to copy link", "error");
    }
  };

  const navigateTo = (tabName) => {
    changePage(tabName);
  };

  const openPreviewModal = (template) => {
    previewTargetTemplate.value = template;
    showPreviewModal.value = true;
  };

  const applyTemplateFromModal = async () => {
    if (!previewTargetTemplate.value) return;
    await applyTemplate(previewTargetTemplate.value.id);
    showPreviewModal.value = false;
  };

  const applyTemplate = async (templateId) => {
    try {
      isApplyingTemplate.value = templateId;
      await store.dispatch("updateWebsiteDetails", { template: templateId });
      await store.dispatch("fetchWebsiteDetails");
      const matched = templateList.find((t) => t.id === templateId);
      showToast(`Visual Theme set to "${matched?.name || templateId}" successfully!`, "success");
    } catch (err) {
      console.error("applyTemplate error:", err);
      showToast(err.message || "Failed to switch website template", "error");
    } finally {
      isApplyingTemplate.value = "";
    }
  };

  const openToggleStatusModal = () => {
    showStatusModal.value = true;
  };

  const executeToggleStatus = async () => {
    try {
      isTogglingStatus.value = true;
      await store.dispatch("toggleWebsiteStatus");
      await store.dispatch("fetchWebsiteDetails");
      showStatusModal.value = false;
      showToast("Website availability updated successfully!", "success");
    } catch (err) {
      console.error("executeToggleStatus error:", err);
      showToast("Failed to update website status", "error");
    } finally {
      isTogglingStatus.value = false;
    }
  };

  const hasUnsavedChanges = computed(() => {
    const currentComparable = { ...form, logo: getComparableImage(form.logo) };
    return JSON.stringify(currentComparable) !== initialStateJson.value;
  });

  function getComparableImage(img) {
    if (img instanceof File) return img.name;
    if (typeof img === "object" && img !== null) return img.url || img.secure_url || "";
    return img || "";
  }

  function snapshotState() {
    const currentComparable = { ...form, logo: getComparableImage(form.logo) };
    initialStateJson.value = JSON.stringify(currentComparable);
  }

  function addNavItem() {
    form.navItems.push({
      label: "New Page",
      url: "#",
      enabled: true,
      sortOrder: form.navItems.length,
    });
  }

  function removeNavItem(index) {
    form.navItems.splice(index, 1);
  }

  function moveNavItem(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= form.navItems.length) return;
    const item = form.navItems.splice(index, 1)[0];
    form.navItems.splice(targetIndex, 0, item);
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details = store.state.websiteDetails || {};

      if (details.schoolName) form.schoolName = details.schoolName;
      if (details.tagline) form.tagline = details.tagline;
      if (details.logo) form.logo = details.logo;
      if (details.establishmentYear || details.foundedYear) {
        form.establishmentYear = details.establishmentYear || details.foundedYear;
      }
      if (details.boardAffiliation) form.boardAffiliation = details.boardAffiliation;
      if (details.affiliationNumber) form.affiliationNumber = details.affiliationNumber;
      if (details.officeHours) form.officeHours = details.officeHours;
      if (details.primaryPhone) form.primaryPhone = details.primaryPhone;
      if (details.email) form.email = details.email;
      if (details.city) form.city = details.city;
      if (details.state) form.state = details.state;
      if (details.address) form.address = details.address;
      if (details.facebook) form.facebook = details.facebook;
      if (details.youtube) form.youtube = details.youtube;
      if (details.portalLink) form.portalLink = details.portalLink;
      if (details.admissionLink) form.admissionLink = details.admissionLink;

      if (Array.isArray(details.navItems) && details.navItems.length > 0) {
        form.navItems = details.navItems.map((n, idx) => ({
          label: n.label || "",
          url: n.url || "#",
          enabled: n.enabled !== false,
          sortOrder: n.sortOrder ?? idx,
        }));
      }

      snapshotState();
    } catch (err) {
      console.error(err);
      showToast("Failed to load header details", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    if (!form.schoolName.trim()) {
      showToast("School Name is required", "error");
      return;
    }

    isSaving.value = true;
    try {
      const payload = {
        logo: form.logo,
        schoolName: form.schoolName,
        tagline: form.tagline,
        establishmentYear: form.establishmentYear,
        foundedYear: form.establishmentYear,
        boardAffiliation: form.boardAffiliation,
        affiliationNumber: form.affiliationNumber,
        officeHours: form.officeHours,
        primaryPhone: form.primaryPhone,
        email: form.email,
        city: form.city,
        state: form.state,
        address: form.address,
        facebook: form.facebook,
        youtube: form.youtube,
        portalLink: form.portalLink,
        admissionLink: form.admissionLink,
        navItems: form.navItems.map((item, idx) => ({
          ...item,
          sortOrder: idx,
        })),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      snapshotState();
      showToast("Header settings updated successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Error saving header configuration", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Form reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      if (activeStudioTab.value === "header") {
        handleSave();
      }
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>
